<template>
  <div>
    <div slot="header" class="header">
      <form-item>
        <span v-if="inappStyles">
          {{ $t('CampaignForm_InApp_InlinePreference_Title') }}
        </span>
        <checkbox
          :title="$t('CampaignForm_InApp_InlinePreference_Title')"
          v-model="value.enabled"
          v-if="!inappStyles"
          data-qa="inline-preference-center-checkbox"
        />
      </form-item>
    </div>
    <div class="body" v-if="value.enabled || inappStyles">
      <form-item>
        <checkbox title="" v-model="value.enabled" v-if="inappStyles" />
      </form-item>
      <form-item
        prop="subscription"
        :label="$t('CampaignForm_InApp_Field_Subscription')"
      >
        <enum-select
          :options="subscriptionLabels"
          v-model="value.subscription"
          data-qa="preference-subscription-dropdown"
        />
      </form-item>

      <draggable
        v-if="onSiteTerms"
        :list="ipcSequenceList"
        class="ipc-draggable-list"
        ghost-class="ipc-ghost"
        handle=".ipc-drag-handle"
        filter=".redactor-wrapper, input"
        :prevent-on-filter="false"
      >
        <div
          v-for="item in ipcSequenceList"
          :key="item.name"
          class="ipc-draggable-item"
        >
          <el-row type="flex" align="middle">
            <el-col :span="22">
              <template v-if="item.name === 'age_confirmation'">
                <form-item>
                  <checkbox
                    class="terms-checkbox"
                    :title="
                      $t(
                        'CampaignForm_Onsite_InlinePreferenceAgeConfirmation_Title'
                      )
                    "
                    v-model="value.age_confirmation_enabled"
                    data-qa="age-confirmation-checkbox"
                  />
                </form-item>
                <form-item
                  prop="age_confirmation_text"
                  v-if="value.age_confirmation_enabled"
                >
                  <el-input
                    v-model="value.age_confirmation_text"
                    type="textarea"
                    data-qa="age-confirmation-input"
                  />
                </form-item>
              </template>

              <template v-else-if="item.name === 'terms'">
                <form-item>
                  <checkbox
                    class="terms-checkbox"
                    :title="
                      $t('CampaignForm_Onsite_InlinePreferenceTerms_Title')
                    "
                    v-model="value.terms_enabled"
                    data-qa="terms-and-conditions-checkbox"
                  />
                </form-item>
                <form-item class="redactor" v-if="value.terms_enabled">
                  <redactor
                    v-model="value.terms_text"
                    :plugins="['link']"
                    :buttons="['link']"
                    key="terms_text"
                    :use-default-plugins="false"
                    :use-default-buttons="false"
                    :custom-config="{
                      tabKey: false,
                      enterKey: false,
                      maxHeight: 50
                    }"
                    data-qa="terms-and-conditions-input"
                  />
                </form-item>
              </template>

              <template v-else-if="item.name === 'opt_in'">
                <form-item
                  prop="opt_in"
                  :label="$t('CampaignForm_InApp_Field_Inline_OptIn_Title')"
                >
                  <el-input
                    v-model="value.opt_in"
                    type="textarea"
                    data-qa="preference-opt-in-input"
                  />
                </form-item>
              </template>

              <template v-else-if="item.name === 'contact_field'">
                <form-item>
                  <checkbox
                    class="terms-checkbox"
                    :title="
                      $t(
                        'CampaignForm_Onsite_InlinePreferenceContactField_Title'
                      )
                    "
                    v-model="value.contact_field"
                    data-qa="contact-field-checkbox"
                  />
                </form-item>
                <form-item v-if="value.contact_field" :label="placeholderLabel">
                  <el-input
                    v-model="value.contact_field_placeholder"
                    :placeholder="defaultPlaceholder"
                    data-qa="contact-field-placeholder-input"
                  />
                </form-item>
              </template>

              <template v-else-if="item.name === 'button'">
                <form-item
                  prop="button"
                  :label="$t('CampaignForm_InApp_Field_Inline_Button_Title')"
                >
                  <el-input
                    v-model="value.button"
                    data-qa="preference-button-input"
                  />
                </form-item>
              </template>
            </el-col>
            <el-col :span="2" class="ipc-drag-handle-col">
              <i class="el-icon-rank ipc-drag-handle" />
            </el-col>
          </el-row>
        </div>
      </draggable>

      <template v-else>
        <form-item
          prop="opt_in"
          :label="$t('CampaignForm_InApp_Field_Inline_OptIn_Title')"
        >
          <el-input
            v-model="value.opt_in"
            type="textarea"
            data-qa="preference-opt-in-input"
          />
        </form-item>
        <form-item
          prop="button"
          :label="$t('CampaignForm_InApp_Field_Inline_Button_Title')"
        >
          <el-input v-model="value.button" data-qa="preference-button-input" />
        </form-item>
      </template>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import Checkbox from '@/common/components/form/Checkbox'
import FormItem from '@/common/components/form/FormItem'
import EnumSelect from '@/common/components/form/EnumSelect'
import Redactor from '@/common/components/Redactor'
import {
  subscriptionLabels,
  subscriptionTypes
} from '@/modules/campaign-manage/enum/InAppTypesEnum'

const DEFAULT_IPC_SEQUENCE = [
  'age_confirmation',
  'terms',
  'opt_in',
  'contact_field',
  'button'
]

export default {
  name: 'InlinePreference',
  components: { draggable, EnumSelect, FormItem, Checkbox, Redactor },
  props: {
    value: {
      type: Object,
      required: true
    },
    inappStyles: {
      type: Boolean,
      default: false
    },
    onSiteTerms: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      subscriptionLabels,
      ipcSequenceList: [],
      ipcInitialized: false
    }
  },
  computed: {
    defaultPlaceholder() {
      if (this.value.subscription === subscriptionTypes.SMS) {
        return this.$t('CampaignForm_Onsite_InlinePreference_PhoneField')
      }
      return this.$t('CampaignForm_Onsite_InlinePreference_EmailField')
    },
    placeholderLabel() {
      if (this.value.subscription === subscriptionTypes.SMS) {
        return this.$t(
          'CampaignForm_Onsite_InlinePreference_PhonePlaceholderLabel'
        )
      }
      return this.$t(
        'CampaignForm_Onsite_InlinePreference_EmailPlaceholderLabel'
      )
    }
  },
  mounted() {
    if (this.value.button === null && this.value.enabled) {
      this.setOptInText()
    }
    if (!this.value.terms_text) {
      this.setTermsText()
    }
    if (!this.value.age_confirmation_text) {
      this.value.age_confirmation_text = this.$t(
        'CampaignForm_Onsite_InlinePreferenceAgeConfirmation_Default'
      )
    }
    if (this.onSiteTerms) {
      this.initIpcSequence()
    }
  },
  methods: {
    setOptInText() {
      if (this.value.subscription === subscriptionTypes.EMAIL) {
        this.value.button = this.$t(
          'CampaignForm_InApp_Field_Inline_Button_Email'
        )
        this.value.opt_in = this.$t(
          'CampaignForm_InApp_Field_Inline_OptIn_Email'
        )
      } else {
        this.value.button = this.$t(
          'CampaignForm_InApp_Field_Inline_Button_SMS'
        )
        this.value.opt_in = this.$t('CampaignForm_InApp_Field_Inline_OptIn_SMS')
      }
    },
    setTermsText() {
      this.value.terms_text = this.$t(
        'CampaignForm_Onsite_InlinePreference_ApproveTerms'
      )
    },
    initIpcSequence() {
      const sequence = this.value.ipc_sequence || DEFAULT_IPC_SEQUENCE
      this.ipcSequenceList = sequence.map(name => ({ name }))
      this.$nextTick(() => {
        this.ipcInitialized = true
      })
    }
  },
  watch: {
    'value.subscription': {
      handler() {
        this.setOptInText()
      }
    },
    ipcSequenceList: {
      handler(list) {
        if (this.onSiteTerms && this.ipcInitialized && list.length > 0) {
          this.value.ipc_sequence = list.map(item => item.name)
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped lang="scss">
.creative {
  background-color: #f8fafc;
  border: 1px solid #dbdee7;
  margin: 10px;
  box-shadow: none;
  .header {
    border-bottom: none;
    .delete-btn {
      float: right;
      padding: 3px 0;
      cursor: pointer;
      font-size: 16px;
      color: #a7acba;
    }
    span {
      font-weight: 600;
      font-size: 14px;
    }
  }
}
/deep/ .el-card__header {
  padding: 16px;
  border-bottom: none;
}

/deep/ .terms-checkbox .el-checkbox__label {
  line-height: 30px;
}
/deep/ .el-form-item {
  margin-top: 10px;
}

.ipc-draggable-list {
  margin-top: 10px;
}

.ipc-draggable-item {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 8px;
}

.ipc-ghost {
  opacity: 0.5;
  background: #e6f7ff;
}

.ipc-drag-handle-col {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ipc-drag-handle {
  cursor: move;
  font-size: 20px;
  color: #909399;
  display: inline-block;

  &:hover {
    color: #409eff;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/setup/InlinePreference.vue